import html2canvas from "html2canvas";
import { saveAs } from "file-saver";
import PptxGenJS from "pptxgenjs";
import moment from "moment-timezone";
import purina_logo from "../../../../../../assets/images/logo-addie-teal.png";
import { SLIDE_DIMENSIONS } from "../constants/chartConstants";

/**
 * Capitalizes first letter of each word in a string
 */
export const capitalizeFirst = (str) => {
  return str.replace(/\b\w/g, (char) => char.toUpperCase());
};

/**
 * Generates footer text for PPT slides
 */
const generateFooterText = (dairyname, subherdSelected) => {
  const todaydate = new Date();
  const today = moment(todaydate).format("l");
  const todaytime = moment(todaydate).format("h:mm a");

  return `Report generated ${today} at ${todaytime} for ${capitalizeFirst(
    dairyname
  )} - ${capitalizeFirst(subherdSelected)}`;
};

/**
 * Gets disclaimer text for PPT slides
 */
const getDisclaimerText = () => {
  return "Disclaimer; Limited Rights: This report includes assumptions and expectations in light of currently available information. Actual analytics regarding dairy cow herd inventories and performance metrics may differ. THIS REPORT IS PROVIDED AS-IS, WHERE-IS, WITH ALL FAULTS. ALL WARRANTIES ARE DISCLAIMED, INCLUDING WITHOUT LIMITATION, ANY WARRANTIES OF MERCHANTABILITY, NON-INFRINGEMENT, TITLE AND FITNESS FOR A PARTICULAR PURPOSE. This report and the Addie™ Insights are confidential information and subject to limited license rights limited to the specified dairy farming operations only. This notice must be recreated on all copies and excerpts of this report.";
};

/**
 * Creates a PowerPoint slide with chart image
 */
const createSlideWithChart = (pptx, imgData, slideConfig) => {
  const { slideWidthInches, slideHeightInches } = slideConfig;

  const slide = pptx.addSlide({
    masterName: "Blank",
    w: slideWidthInches,
    h: slideHeightInches,
  });

  const imgWidthFraction = SLIDE_DIMENSIONS.IMAGE_WIDTH_FRACTION;
  const imgHeightFraction = SLIDE_DIMENSIONS.IMAGE_HEIGHT_FRACTION;

  const imgWidth = slideWidthInches * imgWidthFraction;
  const imgHeight = slideHeightInches * imgHeightFraction;

  const imgX = (slideWidthInches - imgWidth) / 0.9;
  const imgY = (slideHeightInches - imgHeight) / 2;

  slide.addImage({
    data: imgData,
    x: imgX,
    y: imgY,
    w: imgWidth,
    h: imgHeight,
  });

  return slide;
};

/**
 * Adds logo to slide
 */
const addLogoToSlide = (slide, slideHeightInches) => {
  slide.addImage({
    data: purina_logo,
    x: 0.1,
    y: slideHeightInches - 0.3,
    w: "8%",
    h: 40 / 72,
  });
};

/**
 * Adds disclaimer text to slide
 */
const addDisclaimerToSlide = (slide, slideHeightInches) => {
  const disclaimerOptions = {
    x: 0.9,
    y: slideHeightInches - 0.3,
    w: "90%",
    h: 0.3,
    fontSize: 6,
    color: "gray",
    align: "center",
  };

  slide.addText(getDisclaimerText(), disclaimerOptions);
};

/**
 * Adds footer text to slide
 */
const addFooterToSlide = (slide, slideHeightInches, footerText) => {
  const footerOptions = {
    x: 0.9,
    y: slideHeightInches + 0.05,
    w: "90%",
    h: 0.2,
    fontSize: 8,
    color: "gray",
    align: "center",
  };

  slide.addText(footerText, footerOptions);
};

/**
 * Downloads a chart as PowerPoint presentation
 * @param {React.RefObject} chartRef - Reference to the chart container element
 * @param {string} chartName - Name of the chart for filename
 * @param {string} dairyname - Name of the dairy farm
 * @param {Object} subherddata - Subherd data object
 */
export const downloadChartAsPPT = async (
  chartRef,
  chartName,
  dairyname,
  subherddata
) => {
  const chartContainer = chartRef.current;

  if (!chartContainer) {
    console.error("Chart container not found.");
    return;
  }

  try {
    // Generate canvas from chart
    const canvas = await html2canvas(chartContainer);
    if (!canvas) {
      console.error("Canvas not generated.");
      return;
    }

    // Create PowerPoint
    const pptx = new PptxGenJS();
    const slideConfig = {
      slideWidthInches: SLIDE_DIMENSIONS.WIDTH_INCHES,
      slideHeightInches: SLIDE_DIMENSIONS.HEIGHT_INCHES,
    };

    // Convert canvas to image
    const imgData = canvas.toDataURL("image/png");

    // Create slide with chart
    const slide = createSlideWithChart(pptx, imgData, slideConfig);

    // Add logo, disclaimer, and footer
    addLogoToSlide(slide, slideConfig.slideHeightInches);
    addDisclaimerToSlide(slide, slideConfig.slideHeightInches);

    const subherdSelected =
      subherddata.description !== undefined
        ? subherddata.description
        : subherddata.subHerdSelected;

    const footerText = generateFooterText(dairyname, subherdSelected);
    addFooterToSlide(slide, slideConfig.slideHeightInches, footerText);

    // Save file
    const pptxBlob = await pptx.write();
    saveAs(pptxBlob, `${chartName} chart.pptx`);
  } catch (error) {
    console.error("Error downloading chart as PPT:", error);
  }
};
